/**
 * Flatten a CMS rich-text value to plain text.
 *
 * Pure function (no Nuxt auto-imports, no DOM) so it runs on the server and can
 * be unit-tested in isolation.
 *
 * Product titles come from InRiver via
 * `ToHtmlStringWithEmbeddedHtml`, so they are HTML — the rich-text editor wraps
 * every saved value in a `<p>` and encodes symbols as entities
 * (`<p>GentleCath&trade; Glide Male Catheters</p>`). That markup is intentional
 * and must keep rendering wherever a title is *displayed*; use this only where a
 * consumer needs a plain string (query params, JSON-LD, form fields).
 *
 * Unknown named entities are left untouched rather than guessed at — callers
 * that encode their output (e.g. encodeURIComponent) stay correct either way.
 */

// Excluding `<` as well as `>` keeps this linear: a run of tag-body characters
// ends at the next angle bracket either way, so no two start positions can
// backtrack over the same run. With `[^>]+` a string of unclosed `<` is
// quadratic (~277ms for 32k characters).
const HTML_TAG_RE = /<[^<>]+>/g
const NUMERIC_ENTITY_RE = /&#(\d+);/g
const HEX_ENTITY_RE = /&#x([0-9a-f]+);/gi
const NAMED_ENTITY_RE = /&[a-z][a-z0-9]*;/gi
const WHITESPACE_RE = /\s+/g

// `&amp;` is decoded in the same pass as the rest: a single left-to-right
// replace consumes `&amp;` in `&amp;trade;` and resumes after it, so the
// literal text `&trade;` survives instead of decoding twice into `™`.
const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': '\'',
  '&nbsp;': ' ',
  '&trade;': '™',
  '&reg;': '®',
  '&copy;': '©',
  '&deg;': '°',
  '&times;': '×',
  '&rsquo;': '’',
  '&lsquo;': '‘',
  '&ldquo;': '“',
  '&rdquo;': '”',
  '&ndash;': '–',
  '&mdash;': '—',
  '&hellip;': '…',
  '&eacute;': 'é',
  '&Eacute;': 'É',
}

export function htmlToPlainText(html?: string | null): string {
  if (!html)
    return ''

  return String(html)
    .replace(HTML_TAG_RE, ' ')
    .replace(NUMERIC_ENTITY_RE, (_, code) => String.fromCodePoint(Number(code)))
    .replace(HEX_ENTITY_RE, (_, code) => String.fromCodePoint(Number.parseInt(code, 16)))
    .replace(NAMED_ENTITY_RE, m => HTML_ENTITIES[m] ?? m)
    .replace(WHITESPACE_RE, ' ')
    .trim()
}
